import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useT } from '../i18n';
import { L } from './tokens';
import { CloseSearchGlyph } from './searchIcons';
import { MIN_QUERY, RESULTS_PATH } from './searchFormat';

// A simple search bar under the header, in the style of the earlier "coming soon" bar: one white panel with the input and Close.
// No suggestions while typing and nothing is asked of the backend until Enter: Enter opens the results page (/search) and hands it the term in memory (router state), never in the address.
// Close and Esc close it. Works on every page. The term is held in memory only.

export default function SearchBar({ pad, phone, onClose }) {
  const t = useT();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [q, setQ] = useState('');
  const [tooShort, setTooShort] = useState(false);

  useEffect(() => { if (inputRef.current) inputRef.current.focus(); }, []);
  // Esc closes the bar from anywhere on the page.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const submit = (e) => {
    e.preventDefault();
    const term = q.trim();
    if (term.length < MIN_QUERY) { setTooShort(true); return; }
    onClose();
    navigate(RESULTS_PATH, { state: { q: term } });
  };

  return (
    <form role="search" data-testid="search-bar" onSubmit={submit} style={{
      position: 'absolute', left: pad, right: pad, top: 'calc(100% + 12px)', zIndex: 20, background: L.card, border: `1px solid ${L.border}`, borderRadius: 16,
      boxShadow: '0 12px 32px rgba(46, 33, 22, 0.14)', padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 8, fontFamily: L.sans, color: L.text, margin: 0,
    }}>
      <label htmlFor="global-search" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>{t('header.search')}</label>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <CloseSearchGlyph />
        <input
          id="global-search" ref={inputRef} type="text" value={q} autoComplete="off" spellCheck={false} maxLength={64} enterKeyHint="search"
          placeholder={t('search.placeholder')} data-testid="search-input"
          onChange={(e) => { setQ(e.target.value); setTooShort(false); }}
          style={{ flexGrow: 1, minWidth: 0, border: 'none', outline: 'none', font: 'inherit', fontSize: phone ? 16 : 17, color: L.text, background: 'transparent', padding: 0 }}
        />
        <button type="button" onClick={onClose} aria-label={t('search.closeLabel')} style={{ border: 'none', background: 'transparent', cursor: 'pointer', font: 'inherit', fontSize: 14, color: L.muted }}>
          {t('header.close')}
        </button>
      </div>
      {tooShort && <div role="alert" style={{ fontSize: 14, color: L.muted }}>{t('search.tooShort')}</div>}
    </form>
  );
}
